import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  ACCOUNT_CONTACT_SETS,
  Account,
  AccountContactSet,
  FieldData,
  TextField,
  UpdateAccountContactsRequest,
  useForm,
} from 'vopay';
import ContactEditRow from './ContactEditRow';

/** The fields `GET /partner/account` returns per contact, in the order the card lists them. */
const CONTACT_FIELDS = ['FullName', 'Title', 'Email', 'PhoneNumber'] as const;

type ContactField = (typeof CONTACT_FIELDS)[number];

export type ContactFieldName = `${AccountContactSet}${ContactField}`;

/** One flat value per contact and field, named as the update route's parameters. */
export type ContactsFormValues = Record<ContactFieldName, string>;

/**
 * The column widths behind `POST /account/profile/update-contact`
 * (`VIOPartnerAccountManager::CONTACT_MAX_LENGTHS`); the billing email alone is 500 wide.
 */
const MAX_LENGTHS: Record<ContactField, number> = { FullName: 45, Title: 45, Email: 100, PhoneNumber: 13 };
const BILLING_EMAIL_MAX_LENGTH = 500;

/** `VIOValidatorFactory`'s `*ContactFullName` rule: letters, digits, spaces and these marks. */
const NAME_CHARACTERS = /^[\p{L}0-9=$,& \s()./*'-]*$/u;

/** `VIOPhoneNumberValidator`: digits only. */
const DIGITS_ONLY = /^\d*$/;

function fieldName(contactSet: AccountContactSet, field: ContactField): ContactFieldName {
  return `${contactSet}${field}`;
}

/** The account's contacts as the form holds them; a contact the response left out reads as blank. */
export function toContactsFormValues(account: Account | null | undefined): ContactsFormValues {
  const values = {} as ContactsFormValues;

  ACCOUNT_CONTACT_SETS.forEach((contactSet) => {
    CONTACT_FIELDS.forEach((field) => {
      values[fieldName(contactSet, field)] = account?.[contactSet]?.[field] ?? '';
    });
  });

  return values;
}

/**
 * What the save sends: only the fields the user changed. The route is a partial update, so an
 * unchanged field left out stays as it is, and a changed one arrives trimmed.
 */
export function toChangedContacts(values: ContactsFormValues, initialValues: ContactsFormValues) {
  const changes: UpdateAccountContactsRequest = {};

  ACCOUNT_CONTACT_SETS.forEach((contactSet) => {
    CONTACT_FIELDS.forEach((field) => {
      const name = fieldName(contactSet, field);
      const value = values[name].trim();

      if (value !== initialValues[name]) changes[name] = value;
    });
  });

  return changes;
}

type UseContactsFormConfigProps = {
  /** Re-seeds the form whenever the account (re)loads. */
  initialValues: ContactsFormValues;
  onSubmit: (values: ContactsFormValues) => Promise<void>;
};

/**
 * The Contact Information edit form (ID-13964 D1): the four contacts' name, title, email and
 * phone, validated the way `POST /account/profile/update-contact` validates them. Every field is
 * optional, but a value the account already has cannot be blanked: the route ignores an empty
 * parameter, so a cleared field would silently come back on the next load.
 */
export default function useContactsFormConfig({ initialValues, onSubmit }: UseContactsFormConfigProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.DetailsTab.Contacts' });

  const config = useMemo<FieldData<ContactsFormValues>[]>(() => {
    const labels: Record<ContactField, string> = {
      FullName: t('fullName', { defaultValue: 'Name' }),
      Title: t('jobTitle', { defaultValue: 'Title' }),
      Email: t('emailAddress', { defaultValue: 'Email Address' }),
      PhoneNumber: t('phoneNumber', { defaultValue: 'Phone Number' }),
    };

    const placeholders: Record<ContactField, string> = {
      FullName: t('fullNamePlaceholder', { defaultValue: 'Enter Full Name' }),
      Title: t('jobTitlePlaceholder', { defaultValue: 'Enter Title' }),
      Email: t('emailAddressPlaceholder', { defaultValue: 'Enter Email Address' }),
      PhoneNumber: t('phoneNumberPlaceholder', { defaultValue: 'Enter Phone Number' }),
    };

    const maxLength = (contactSet: AccountContactSet, field: ContactField) =>
      contactSet === 'BillingContact' && field === 'Email' ? BILLING_EMAIL_MAX_LENGTH : MAX_LENGTHS[field];

    /**
     * Only a value the user changed is validated: only changed fields are sent, so what the account
     * already holds — a phone stored with its formatting, say — cannot be refused and is not flagged.
     */
    const validate = (contactSet: AccountContactSet, field: ContactField) => {
      const name = fieldName(contactSet, field);

      let schema = Yup.string()
        .label(labels[field])
        .max(maxLength(contactSet, field))
        .test(
          'keeps-a-saved-value',
          t('cannotClear', { defaultValue: 'A saved value cannot be cleared; enter a replacement.' }),
          (value) => !initialValues[name] || Boolean(value?.trim()),
        );

      if (field === 'FullName') {
        schema = schema.matches(NAME_CHARACTERS, {
          message: t('nameCharacters', {
            defaultValue: "Only letters, numbers, spaces and = $ , & ( ) . * - ' / are allowed.",
          }),
        });
      }

      if (field === 'Email') schema = schema.email();

      if (field === 'PhoneNumber') {
        schema = schema.matches(DIGITS_ONLY, {
          message: t('phoneDigitsOnly', { defaultValue: 'Enter digits only, with no spaces, dashes or parentheses.' }),
        });
      }

      return Yup.lazy((value) => (value === initialValues[name] ? Yup.string() : schema));
    };

    return ACCOUNT_CONTACT_SETS.flatMap((contactSet) =>
      CONTACT_FIELDS.map(
        (field): FieldData<ContactsFormValues> => ({
          fieldName: fieldName(contactSet, field),
          label: () => labels[field],
          getInputState: () => 'optional',
          showField: () => true,
          validate: () => validate(contactSet, field),
          renderField: ({ getFormikData }) => (
            <TextField
              {...getFormikData<ContactsFormValues[ContactFieldName]>({})}
              // No `tel` preset for the phone: it would insert the dashes the route refuses.
              preset={field === 'Email' ? 'email' : undefined}
              variant="outlineShadow"
              placeholder={placeholders[field]}
              maxLength={maxLength(contactSet, field)}
              // The messages run to two lines in the card's narrow column; a one-line box clips them.
              errorFitContent
              isAutoCompleteDisabled
            />
          ),
        }),
      ),
    );
  }, [t, initialValues]);

  const { ctx, renderFormElements } = useForm<ContactsFormValues>({
    initialValues,
    config,
    onSubmit,
    enableReinitialize: true,
  });

  /** One contact's rows, each a label beside its input, in the card that edits that contact. */
  const renderContactFields = (contactSet: AccountContactSet) =>
    renderFormElements({
      config: config.filter((field) => field.fieldName.startsWith(contactSet)),
      override: ({ fieldName: name, label, template }) => (
        <ContactEditRow key={name} label={label} data-testid={`contactField-${name}`}>
          {template}
        </ContactEditRow>
      ),
    });

  return { ctx, renderContactFields };
}
